/**
 * The live connection: JMAP `StateChange` over server-sent events.
 *
 * # Why this is not a poll any more
 *
 * It was one for a plain reason: `EventSource` cannot send an `Authorization`
 * header, and the credential used to be a device token. The ways round that
 * were a token in the query string -- which puts a credential in every proxy
 * log and in the referrer of anything the page later loads -- or reading the
 * stream with `fetch`, which means writing the framing, the reconnect and the
 * `Last-Event-ID` bookkeeping by hand and getting all three right. The session
 * cookie removed the obstacle rather than working around it: a browser attaches
 * cookies to an `EventSource` by itself, so the ordinary API is now the one that
 * works. `withCredentials` is deliberately not set -- that flag is about CORS,
 * and this is same-origin.
 *
 * # That reasoning was right, and it was about a browser
 *
 * There is a second build now, its requests are issued outside the page, and no
 * cookie is ever sent. The obstacle the cookie removed came straight back for
 * it: the daemon answered 401, `EventSource` closed for good, and the recovery
 * below re-checked the credential over the *other* transport -- where it was
 * perfectly valid -- and rescheduled the same refusal for the life of the app.
 * A loop that delivered nothing and reported nothing.
 *
 * So **making the socket now belongs to the transport** and everything else here
 * stays shared: the endpoint, the backoff, the handover, what visibility and
 * `online` mean, and the coalescing at the bottom. That is twenty-odd lines
 * moved out of three hundred, and the ratio is the point -- the hard part of
 * this file was never the socket.
 *
 * # The event is named, and that is not a detail
 *
 * The daemon emits RFC 8887 `StateChange` as a `state` event, so the listener is
 * `addEventListener("state", ...)`. `onmessage` fires only for an event with no
 * name and would never fire here -- which is the single most likely way to build
 * this, watch the network tab show a healthy stream, and have the app silently
 * do nothing.
 *
 * # `ping` is not optional
 *
 * nginx closes an idle upstream stream at `proxy_read_timeout 600s`. Without a
 * keepalive the connection dies every ten minutes -- the browser reconnects, so
 * nothing looks broken, and the app instead spends its life in a slow
 * reconnect loop nobody can see. The daemon clamps the request to 10-300s and
 * answers with comment frames, which no event handler ever sees.
 *
 * # The failure that has no status code
 *
 * Per the HTML spec an `EventSource` whose request returns a non-200 fails
 * *permanently*: `readyState` goes to `CLOSED`, one `error` fires, and the
 * browser never retries. So a revoked session -- the daemon's 401 -- kills the
 * stream for the rest of the page's life, and a client that ignored that would
 * sit on a dead socket and never update again. That is strictly worse than the
 * poll it replaced, because the poll at least kept trying.
 *
 * The error event carries no status, so "the daemon restarted" and "I am signed
 * out" arrive looking identical. They are told apart by asking the one endpoint
 * that answers the question -- `/auth/whoami`, through `auth.refreshIdentity`,
 * which owns that signal -- and only in the case where the server demonstrably
 * answered. See {@link onError}.
 *
 * # What an event means
 *
 * A wake-up, not data: the payload carries no mail. Everything it can say is
 * "something changed" -- and not even which of the things it could have been,
 * since the daemon polls one counter shared by every object type. So the
 * response is to *ask*: `lib/delta.ts` owns that, and this module owns only
 * when to run it. It used to be a full refresh, which during a backfill meant
 * re-downloading the whole list every two seconds to be told that mail nobody
 * is looking at had moved.
 */

import { computed, effect, signal } from "@preact/signals";
// The transport seam. What makes the socket differs between builds; everything
// this module does about a broken one does not. See `lib/net.types.ts`.
import { openStream } from "@transport";

import * as auth from "./auth";
import type { Stream } from "./net.types";
import { session } from "./store";
import type { Session } from "./types";

/**
 * Seconds between keepalives. Well under nginx's ten-minute idle timeout and
 * well inside the daemon's 10-300s clamp, so neither end has to be reconfigured
 * for the other.
 */
const PING = 30;

/** First retry delay, and the ceiling it doubles towards. */
const RETRY_BASE = 2_000;
const RETRY_CAP = 60_000;

/**
 * Failed connection attempts left to the transport before this module takes
 * over -- when the transport retries at all.
 *
 * A stream that dies below HTTP puts a browser `EventSource` back into
 * `CONNECTING` and the browser retries by itself -- with `Last-Event-ID`,
 * which the daemon honours and which makes the gap lossless. That is worth
 * keeping. What is not worth keeping is its fixed few-second interval against
 * a network that is black-holing us, so after a few goes the connection is
 * closed and reopened on the backoff below.
 *
 * Whether there is any native retry to hand over *to* is the transport's own
 * fact (`Stream.retries`), and it is consulted rather than assumed. The native
 * stream reports one error and ends -- no retry, on purpose -- and this module
 * standing aside for it anyway is how both desktops fell silent at the first
 * daemon restart: one `answered: false`, one early return, and nothing left
 * running but the five-minute backstop.
 */
const HANDOVER = 3;

export type StreamStatus = "idle" | "connecting" | "live" | "waiting";

const status = signal<StreamStatus>("idle");

/** What the connection is doing. `waiting` means a retry is scheduled. */
export const streamStatus = computed(() => status.value);

/**
 * Whether this tab is being told about changes as they happen.
 *
 * Exported for Web Push: a live stream is the signal that this device is
 * already watching, and a device that is already watching does not need to be
 * woken by a notification. Read it, do not write it.
 */
export const streamLive = computed(() => status.value === "live");

interface Endpoint {
  url: string;
  /**
   * What a change of stream would mean. The account list is captured by the
   * daemon when the stream connects, so an account added later is not on it and
   * only a reconnect will pick it up. The session's `state` string is
   * deliberately *not* part of this: it changes on every event, and keying on it
   * would reconnect the stream every time it delivered something.
   */
  key: string;
}

let source: Stream | null = null;
let endpoint: Endpoint | null = null;
let retry: ReturnType<typeof setTimeout> | undefined;
let attempts = 0;
/** Bumped by every open and every close; see `open()`. */
let generation = 0;

let wake: () => Promise<void> = async () => {};
let refreshing = false;
let queued = false;

/**
 * Start streaming, and keep streaming, until the returned function is called.
 *
 * `onChange` is what an event means to the app; this module decides only when
 * to run it. Call it from a signed-in app and stop it on the way out: a socket
 * left open on a session that has just been revoked is a connection the daemon
 * has to keep answering for a credential the user has asked it to forget.
 */
export function connect(onChange: () => Promise<void>): () => void {
  wake = onChange;

  // Runs again whenever the session document changes, which is after every
  // refresh -- so the comparison, not the effect, is what decides to reconnect.
  const watching = effect(() => {
    const next = endpointFor(session.value);
    if (next?.key === endpoint?.key) return;
    endpoint = next;
    close();
    if (next) open(next.url);
    else status.value = "idle";
  });

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);

  return () => {
    watching();
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
    close();
    endpoint = null;
    attempts = 0;
    status.value = "idle";
  };
}

/**
 * Fill in the URL the session advertises.
 *
 * Read from the session rather than hardcoded, because it is an RFC 6570
 * template the server is entitled to change -- and because a deployment that
 * serves the daemon under a prefix says so here and nowhere else.
 *
 * `types` is `*`: the daemon accepts the parameter and ignores it today, since
 * every type shares one state string, but `*` is what this client would mean if
 * it ever stopped ignoring it. `closeafter` is empty because a stream that ends
 * after the first event is a long-poll wearing a costume.
 */
function endpointFor(current: Session | null): Endpoint | null {
  if (!current) return null;
  // A daemon that advertises no event source gets no stream, and the backstop
  // timer in `app.tsx` is what the app is left with. That is a better answer
  // than an `EventSource` pointed at the empty string, which resolves to this
  // page and would retry against an HTML document for the rest of the session.
  if (!current.eventSourceUrl) return null;
  const url = current.eventSourceUrl
    .replace("{types}", encodeURIComponent("*"))
    .replace("{closeafter}", "")
    .replace("{ping}", String(PING));
  return { url, key: `${url} ${Object.keys(current.accounts).sort().join(",")}` };
}

function open(url: string): void {
  close();
  if (!navigator.onLine) {
    // `navigator.onLine` is only trustworthy in one direction: true does not
    // mean the daemon is reachable, but false does mean nothing will reach it.
    // The `online` handler opens instead of burning a retry here.
    status.value = "waiting";
    return;
  }
  status.value = "connecting";
  /*
   * Which stream this is, so a late callback from a replaced one is ignored.
   *
   * This used to compare the `EventSource` against the current `source`, which
   * worked because the object existed before any listener could be attached to
   * it. Through the transport the handlers are passed *into* the constructor, so
   * there is no object to compare against yet -- and a number that is captured
   * before the call has no such window. `close()` bumps it too, so a socket that
   * was torn down cannot speak for the one that replaced it.
   */
  const mine = (generation += 1);
  const current = () => mine === generation;
  source = openStream(url, {
    onOpen: () => {
      if (!current()) return;
      attempts = 0;
      status.value = "live";
    },
    onState: () => {
      if (current()) void refresh();
    },
    onError: (answered) => {
      if (current()) onError(answered);
    },
  });
}

function close(): void {
  clearTimeout(retry);
  retry = undefined;
  // Orphan anything still in flight, whether or not there is a socket to shut:
  // a stream closed between a callback being queued and running would otherwise
  // still be able to move `status` after the app had stopped listening.
  generation += 1;
  if (!source) return;
  source.close();
  source = null;
}

function onError(answered: boolean): void {
  attempts += 1;
  status.value = "waiting";

  // `answered` means the server replied and the reply was not an event stream:
  // a 401 from the daemon, a 502 from nginx with the daemon down, a 500. Nothing
  // will retry that by itself, so from here on the retrying is this module's
  // job. Otherwise the connection failed below HTTP -- and whether anything is
  // retrying it already is the transport's fact, not this module's guess. A
  // browser `EventSource` is, and is left to it for a few attempts because its
  // retry carries a `Last-Event-ID` and this one cannot; the native stream is
  // not, ever, and waiting for it is waiting forever. A missing source gets no
  // benefit of the doubt for the same reason: the wrong guess in that direction
  // is a silent stream, and the wrong guess in this one is one redundant dial.
  //
  // The interpretation lives in the transport now -- a browser reads
  // `answered` off `readyState`, and a build holding the stream natively knows
  // it from the response it got -- because it is the one part of this that
  // depends on how the socket was made. See `lib/net.types.ts`.
  if (!answered && source?.retries === true && attempts < HANDOVER) return;

  close();
  void recover(answered);
}

async function recover(answered: boolean): Promise<void> {
  // Only worth asking when the server answered. A stream that died below HTTP
  // -- dropped wifi, a closed lid, a proxy giving up -- is no evidence at all
  // about the credential, and `refreshIdentity` would report `unreachable` and
  // replace the whole app with "cannot reach the daemon" over a blip.
  if (answered) {
    await auth.refreshIdentity();
    // Signed out: the app is already showing the login screen. Reconnecting on
    // a credential the daemon has refused would only feed its failure counter.
    if (auth.authState.peek() === "out") {
      status.value = "idle";
      return;
    }
  }
  schedule();
}

function schedule(): void {
  const target = endpoint;
  if (!target) return;
  status.value = "waiting";
  clearTimeout(retry);
  retry = setTimeout(() => open(target.url), backoff());
}

function backoff(): number {
  const flat = Math.min(RETRY_CAP, RETRY_BASE * 2 ** Math.max(0, attempts - 1));
  // Jittered, so a daemon coming back up is not met by every tab in the house
  // reconnecting in the same millisecond.
  return Math.round(flat * (0.75 + Math.random() * 0.5));
}

/** Reconnect now, discarding whatever backoff was in progress. */
function reopen(): void {
  // Never while signed out. There is a moment between this module giving up on
  // a revoked session and the app tearing the connection down, and a tab
  // brought back to the front in that moment would otherwise open a stream the
  // daemon can only refuse.
  if (!endpoint || auth.authState.peek() === "out") return;
  attempts = 0;
  open(endpoint.url);
}

function onOnline(): void {
  reopen();
}

function onOffline(): void {
  // The socket is already useless; closing it stops the browser retrying into
  // a network that is not there and keeps `streamLive` honest.
  close();
  status.value = "waiting";
}

function onVisibility(): void {
  if (document.visibilityState !== "visible") return;
  if (auth.authState.peek() === "out") return;
  // A hidden tab keeps its stream -- hearing about mail while in the background
  // is the point -- but a browser is free to freeze the tab, and a frozen
  // socket comes back looking open. So coming back reconnects when the stream
  // is not live, and refreshes either way: one request when someone looks at
  // the window is not a poll, and it is the only way to catch a stream that is
  // open and dead.
  if (status.peek() !== "live") reopen();
  void refresh();
}

/**
 * Run the app's refresh, one at a time.
 *
 * The daemon can emit as often as every two seconds -- a sync run landing four
 * hundred messages does exactly that -- and a refresh takes longer than that on
 * a slow link. Overlapping them would stack requests that each replace the same
 * list. At most one runs, and at most one is remembered, because the second of
 * two queued refreshes would ask the same question as the first.
 */
async function refresh(): Promise<void> {
  if (refreshing) {
    queued = true;
    return;
  }
  refreshing = true;
  try {
    await wake();
  } catch {
    // The callback reports its own failures; a refresh that failed is not a
    // reason to tear down a working stream.
  } finally {
    refreshing = false;
  }
  if (!queued) return;
  queued = false;
  void refresh();
}
